import { getDB } from "../db";
import { ghlAgencyTokens } from "../../../drizzle/schema";
import { and, eq } from "drizzle-orm";
import { getCompanyAccessToken } from "./get-company-access-token";
import { createLocationToken } from "./create-location-token";
import { server$ } from "@builder.io/qwik-city";
import { EnvKey } from "../utils";
import { EnvGetter } from "@builder.io/qwik-city/middleware/request-handler";

// Cache structure to store tokens with their expiry
type TokenCache = {
  accessToken: string;
  expiresAt: Date;
};

// In-memory cache for tokens
const tokenCache = new Map<string, TokenCache>();

export const getLocationAccessToken = server$(async function (
  locationId: string,
  env: EnvGetter,
): Promise<string> {
  // Check cache first
  const cachedToken = tokenCache.get(locationId);
  if (cachedToken && cachedToken.expiresAt > new Date()) {
    return cachedToken.accessToken;
  }

  // If not in cache or expired, get from database
  const db = getDB();
  const token = await db.query.ghlAgencyTokens.findFirst({
    where: and(
      eq(ghlAgencyTokens.locationId, locationId),
      eq(ghlAgencyTokens.tokenType, "Location"),
    ),
  });

  // If token in DB is still valid, cache and return it
  if (token && token.expiresAt > new Date()) {
    tokenCache.set(locationId, {
      accessToken: token.accessToken,
      expiresAt: token.expiresAt,
    });
    return token.accessToken;
  }

  const companyId = import.meta.env.VITE_GHL_COMPANY_ID!;
  const clientId = env.get(EnvKey.GHL_APP_CLIENT_ID)!;
  const clientSecret = env.get(EnvKey.GHL_APP_CLIENT_SECRET)!;

  // Get company token to create a new location token
  const companyToken = await getCompanyAccessToken(
    companyId,
    clientId,
    clientSecret,
  );

  // Create new location token
  const locationToken = await createLocationToken(
    locationId,
    companyId,
    companyToken,
  );

  // Calculate expiry from locationToken.expires_in through epoch
  const expiresAt = new Date(Date.now() + locationToken.expires_in * 1000);

  // Remove any existing tokens for this location
  await db
    .delete(ghlAgencyTokens)
    .where(
      and(
        eq(ghlAgencyTokens.locationId, locationId),
        eq(ghlAgencyTokens.tokenType, "Location"),
      ),
    );

  // Create new token record
  await db.insert(ghlAgencyTokens).values({
    accessToken: locationToken.access_token,
    refreshToken: "", // Location tokens don't have refresh tokens
    expiresAt,
    companyId,
    locationId,
    userId: locationToken.userId ?? null,
    tokenType: "Location",
  });

  // Update cache
  tokenCache.set(locationId, {
    accessToken: locationToken.access_token,
    expiresAt,
  });

  return locationToken.access_token;
});
